import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const BeforeAfterBetweenLetters: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [correctAnswer, setCorrectAnswer] = useState(" ");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Before, After & Between Letters – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateQuestion = () => {
        const randomIndex = Math.floor(Math.random() * (alphabets.length - 2)) + 1;
        const type = Math.floor(Math.random() * 3); // 0 = Before, 1 = After, 2 = Between
        let newQuestion = "";
        let correct = "";

        if (type === 0) {
            newQuestion = `What letter comes before '${alphabets[randomIndex]}'?`;
            correct = alphabets[randomIndex - 1];
        } else if (type === 1) {
            newQuestion = `What letter comes after '${alphabets[randomIndex]}'?`;
            correct = alphabets[randomIndex + 1];
        } else {
            newQuestion = `Which letter is between '${alphabets[randomIndex - 1]}' and '${alphabets[randomIndex + 1]}'?`;
            correct = alphabets[randomIndex];
        }

        setQuestion(newQuestion);
        setCorrectAnswer(correct);

        const optionsArray = [
            correct,
            alphabets[(randomIndex + 3) % alphabets.length],
            alphabets[(randomIndex + 5) % alphabets.length],
            alphabets[(randomIndex + 7) % alphabets.length],
        ];
        setOptions(shuffleArray(optionsArray));
    };

    const shuffleArray = (array: string[]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion();
                setShowImage(false);
            }, 5000);
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids learn alphabetical order with the "Before, After & Between Letters" activity on Mamas Guidance—designed to build early literacy skills through fun, interactive letter sequencing exercises.' />
            </Helmet>
            <div className="contentPart additionWord matchImage letterOption match-container" style={{ textAlign: "center", padding: "20px" }}>
                {
                    showImage === false ?
                        <>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Before, After & Between Letters</h1>

                                    <p>
                                        Help your child learn the alphabet in sequence with this engaging <strong>Before, After & Between Letters</strong> worksheet. It’s a fun and effective way for nursery and preschool kids to practice <strong>letter order</strong> and strengthen early literacy skills.
                                    </p>

                                    <h2>🔤 What’s Inside the Worksheet?</h2>
                                    <p>
                                        This worksheet features simple exercises where children are shown letters and asked to write:
                                    </p>
                                    <ul>
                                        <li>The letter that comes <strong>before</strong></li>
                                        <li>The letter that comes <strong>after</strong></li>
                                        <li>The letter that comes <strong>between</strong> two given letters</li>
                                    </ul>
                                    <p>
                                        These activities help reinforce <strong>alphabetical sequencing</strong>, <strong>visual memory</strong>, and <strong>attention to detail</strong>.
                                    </p>

                                    <h2>🎯 What Your Child Will Learn</h2>
                                    <ul>
                                        <li>Recognize letters in <strong>alphabetical order</strong> (A–Z)</li>
                                        <li>Identify what comes before or after a given letter</li>
                                        <li>Fill in missing letters in a <strong>sequence</strong></li>
                                        <li>Develop pattern <strong>recognition and writing skills</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 Teaching Tips</h2>
                                    <ul>
                                        <li>Review the ABC song before starting the worksheet.</li>
                                        <li>Use letter blocks or flashcards to demonstrate letter order.</li>
                                        <li>Encourage your child to say each letter aloud as they solve it.</li>
                                        <li>For extra support, keep an alphabet chart nearby as a visual guide.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Object With Letter</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/circle-correct-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Circle Correct Letter</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Object With Letter</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why Letter Sequencing Is Important</h2>
                                    <p>
                                        <strong>Understanding</strong> the order of letters is essential for both reading and writing. <strong>Sequencing</strong> helps kids know how words are formed and improves memory and logic. This worksheet makes <strong>alphabet mastery playful</strong> and <strong>effective</strong>!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Before, After & Between Letters</h1>

                                <p>
                                    Help your child learn the alphabet in sequence with this engaging <strong>Before, After & Between Letters</strong> worksheet. It’s a fun and effective way for nursery and preschool kids to practice <strong>letter order</strong> and strengthen early literacy skills.
                                </p>

                                <h2>🔤 What’s Inside the Worksheet?</h2>
                                <p>
                                    This worksheet features simple exercises where children are shown letters and asked to write:
                                </p>
                                <ul>
                                    <li>The letter that comes <strong>before</strong></li>
                                    <li>The letter that comes <strong>after</strong></li>
                                    <li>The letter that comes <strong>between</strong> two given letters</li>
                                </ul>
                                <p>
                                    These activities help reinforce <strong>alphabetical sequencing</strong>, <strong>visual memory</strong>, and <strong>attention to detail</strong>.
                                </p>

                                <h2>🎯 What Your Child Will Learn</h2>
                                <ul>
                                    <li>Recognize letters in <strong>alphabetical order</strong> (A–Z)</li>
                                    <li>Identify what comes before or after a given letter</li>
                                    <li>Fill in missing letters in a <strong>sequence</strong></li>
                                    <li>Develop pattern <strong>recognition and writing skills</strong></li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips</h2>
                                <ul>
                                    <li>Review the ABC song before starting the worksheet.</li>
                                    <li>Use letter blocks or flashcards to demonstrate letter order.</li>
                                    <li>Encourage your child to say each letter aloud as they solve it.</li>
                                    <li>For extra support, keep an alphabet chart nearby as a visual guide.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Object With Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/circle-correct-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Circle Correct Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Object With Letter</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Letter Sequencing Is Important</h2>
                                <p>
                                    <strong>Understanding</strong> the order of letters is essential for both reading and writing. <strong>Sequencing</strong> helps kids know how words are formed and improves memory and logic. This worksheet makes <strong>alphabet mastery playful</strong> and <strong>effective</strong>!
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="mt-4 mb-5 grid-container object-letter" >
                                {options.map((option, index) => (
                                    <div className="optionButton background-blue" key={option} onClick={() => checkAnswer(option)}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </>
                        :
                        <>
                            <div className="contentPart showMiddle">
                                <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                            </div>
                        </>
                }
            </div>

        </>
    );
};

export default BeforeAfterBetweenLetters;